import { Box, Chip, Modal, Stack, SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useCallback, useMemo, useRef } from "react";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import { FileItem } from "@/common/fileItem";
import { FileHelper } from "@/common/helpers/file";
import {
  GeneralDeferredValueStatus,
  VehicleArea,
  VehicleCleanlinessExtent,
  VehicleDamageExtent,
  VehiclePartType,
  VehicleProjection,
} from "@/core/api/generated/v1";

import AppDropdownIconButton from "../Button/AppDropdownIconButton";
import AppIconButton from "../Button/AppIconButton";
import VehicleCleanlinessExtentInline from "../Entity/Vehicle/VehicleCleanlinessExtentInline";
import VehicleDamageExtentInline from "../Entity/Vehicle/VehicleDamageExtentInline";
import InlineApiEnumValue from "../Enum/InlineApiEnumValue";
import AttachmentCaptionInput from "../Files/AttachmentCaptionInput";
import FileItemView from "../Files/FileItemView";
import BooleanValue from "../Form/Display/BooleanValue";
import FieldValue from "../Form/Display/FieldValue";
import AppIcon from "../Icons/AppIcon";
import AppTypography from "../Text/AppTypography";
import AppTooltip from "../Tooltip/AppTooltip";
import ZoomableBlock from "./ZoomableBlock";

export interface FullScreenFileViewerV2Props {
  files?: FileItem[];
  selectedFileId?: string;
  actions?: (file: FileItem) => FullScreenFileActions;
  onSelectFile?: (file: FileItem) => void;
  onClose: () => void;
  sx?: SxProps<Theme>;
  title?: string;
}

export interface FullScreenFileActions {
  setCaption?: boolean;
  onSetCaption?: (caption: string | undefined) => void;
}
export default function FullScreenFileViewerV2({
  files,
  selectedFileId,
  actions,
  onSelectFile,
  onClose,
  sx,
  title = "",
}: FullScreenFileViewerV2Props) {
  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

  const fileItems = useMemo(() => files || [], [files]);
  const isOpen = useMemo(() => fileItems.length > 0, [files]);
  const selectedFile = useMemo(
    () => fileItems.find((x) => x.id === selectedFileId),
    [files, selectedFileId],
  );

  const selectFile = useCallback(
    (file: FileItem) => {
      onSelectFile && onSelectFile(file);
    },
    [onSelectFile],
  );

  const downloadFile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedFile?.blob) {
        FileHelper.downloadBlob(selectedFile.blob, selectedFile.blob.name);
      } else if (selectedFile?.file) {
        FileHelper.downloadFileByUrl(
          selectedFile.file.url!,
          selectedFile.file.displayFileName ||
            selectedFile.file.originalFileName ||
            selectedFile.file.fileName!,
        );
      }
    },
    [selectedFile],
  );

  const vehicleInsights = useMemo(
    () =>
      selectedFile?.attachment?.vehicleInsights &&
      selectedFile?.attachment?.vehicleInsights.status === GeneralDeferredValueStatus.Success
        ? selectedFile?.attachment?.vehicleInsights.value
        : undefined,
    [selectedFile],
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "background.blur",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          gap: 1,
          ...(sx as unknown as any),
        }}
        onClick={(e) => {
          onClose();
        }}
      >
        {/* Selected file viewer */}
        <Box
          sx={{
            width: "100%",
            height: "auto",
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            overflow: "hidden",
            pt: 2,
          }}
        >
          {selectedFile && (
            <>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                }}
                sx={{
                  position: "relative",
                  width: { xxs: "95%", md: "unset" },
                  maxWidth: { xxs: "95%", md: "90%", lg: "90%" },
                  height: "auto",
                  maxHeight: { xxs: "100%", md: "100%", lg: "100%" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  gap: 1,
                  backgroundColor: (th) => (th.palette.mode === "dark" ? "#E5E8EF" : "#E5E8EF"),
                  borderRadius: "20px",
                  p: 1,

                  "& .react-transform-wrapper": {
                    borderRadius: "10px",
                    maxHeight: "fill-available",
                    display: "flex",
                    m: "auto",

                    "& .react-transform-component": {
                      minWidth: "300px",
                      width: "unset",
                      height: "unset",

                      "& :first-child": {
                        left: 0,
                        right: 0,
                        margin: "auto",
                        width: "100%",
                      },
                    },
                  },
                }}
              >
                <Stack
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  {title && (
                    <AppTypography variant='h5' ellipsing={{ enabled: true }}>
                      {title}
                    </AppTypography>
                  )}

                  {/* Fullscreen actions must be outside from ZoomableBlock */}
                  <Stack className='controls' sx={{ flexDirection: "row", gap: 0.5, ml: "auto" }}>
                    <AppIconButton
                      size='large'
                      variant='contained'
                      tooltipProps={{
                        title:
                          "You can use the mouse to zoom in/out and navigate through the image by dragging the image",
                        placement: "top",
                      }}
                    >
                      <AppIcon of='info' fontSize='small' />
                    </AppIconButton>

                    {vehicleInsights && (
                      <AppTooltip title='Vehicle image insights generated by AI' placement='top'>
                        <AppDropdownIconButton
                          key={selectedFile.id}
                          size='large'
                          variant='contained'
                          dropdownContent={
                            <Box sx={{ minWidth: 300 }}>
                              <FieldValue
                                label='Is Vehicle'
                                valueAlight={{ vertical: "center", horizontal: "end" }}
                                labelAlight={{ vertical: "center" }}
                              >
                                <Chip
                                  label={<BooleanValue value={vehicleInsights?.isVehicle} />}
                                  color={vehicleInsights?.isVehicle ? "success" : "warning"}
                                />
                              </FieldValue>
                              {vehicleInsights?.isVehicle && (
                                <>
                                  <FieldValue
                                    label='Vehicle Area'
                                    isEmpty={vehicleInsights?.vehicleArea === VehicleArea.None}
                                    valueAlight={{ vertical: "center", horizontal: "end" }}
                                    labelAlight={{ vertical: "center" }}
                                  >
                                    <Chip
                                      label={
                                        <InlineApiEnumValue
                                          type='VehicleArea'
                                          value={vehicleInsights?.vehicleArea}
                                        />
                                      }
                                    />
                                  </FieldValue>
                                  <FieldValue
                                    label='Projection'
                                    isEmpty={
                                      vehicleInsights?.vehicleProjection === VehicleProjection.None
                                    }
                                    valueAlight={{ vertical: "center", horizontal: "end" }}
                                    labelAlight={{ vertical: "center" }}
                                  >
                                    <Chip
                                      label={
                                        <InlineApiEnumValue
                                          type='VehicleProjection'
                                          value={vehicleInsights?.vehicleProjection}
                                        />
                                      }
                                    />
                                  </FieldValue>
                                  <FieldValue
                                    label='Cleanliness'
                                    isEmpty={
                                      vehicleInsights.vehicleCleanlinessExtent ===
                                      VehicleCleanlinessExtent.None
                                    }
                                    valueAlight={{ vertical: "center", horizontal: "end" }}
                                    labelAlight={{ vertical: "center" }}
                                  >
                                    <VehicleCleanlinessExtentInline
                                      value={vehicleInsights?.vehicleCleanlinessExtent}
                                    />
                                  </FieldValue>
                                  <FieldValue
                                    label='Damage Extent'
                                    isEmpty={
                                      vehicleInsights.vehicleDamageExtent ===
                                      VehicleDamageExtent.None
                                    }
                                    valueAlight={{ vertical: "center", horizontal: "end" }}
                                    labelAlight={{ vertical: "center" }}
                                  >
                                    <VehicleDamageExtentInline
                                      value={vehicleInsights?.vehicleDamageExtent}
                                    />
                                  </FieldValue>
                                  {vehicleInsights.vehiclePotentialDamages?.length !== 0 && (
                                    <Stack sx={{ p: 1 }}>
                                      <AppTypography sx={{ px: 1 }} variant='subtitle2'>
                                        Potential Damages
                                      </AppTypography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                          px: 1,
                                          mt: 1,
                                          maxWidth: 300,
                                        }}
                                      >
                                        {vehicleInsights?.vehiclePotentialDamages?.map((d, i) => (
                                          <Chip
                                            key={String(d.partType! + d.damageType! + i)}
                                            label={
                                              <Stack direction='row'>
                                                {d.partType &&
                                                  d.partType !== VehiclePartType.None && (
                                                    <InlineApiEnumValue
                                                      type='VehiclePartType'
                                                      value={d.partType}
                                                      sx={{ mr: 1 }}
                                                    />
                                                  )}
                                                {(!d.partType ||
                                                  d.partType === VehiclePartType.None) && (
                                                  <InlineApiEnumValue
                                                    type='VehiclePartCategory'
                                                    value={d.partCategory}
                                                    sx={{ mr: 1 }}
                                                  />
                                                )}
                                                {"-"}
                                                <InlineApiEnumValue
                                                  type='DamageTypeCategory'
                                                  value={d.damageType}
                                                  sx={{ ml: 1 }}
                                                />
                                              </Stack>
                                            }
                                            color='warning'
                                            sx={{ width: "fit-content" }}
                                          />
                                        ))}
                                      </Box>
                                    </Stack>
                                  )}
                                </>
                              )}
                            </Box>
                          }
                        >
                          <AppIcon of='sparkles' fontSize='small' />
                        </AppDropdownIconButton>
                      </AppTooltip>
                    )}

                    {actions?.(selectedFile)?.setCaption && (
                      <AttachmentCaptionInput
                        key={selectedFile.id}
                        dropdownIconButtonProps={{
                          size: "large",
                          variant: "contained",
                        }}
                        defaultCaption={selectedFile.caption}
                        tooltipProps={{ isEnabled: true, title: "Set caption", placement: "top" }}
                        onSetCaption={(value) => actions?.(selectedFile).onSetCaption?.(value)}
                      />
                    )}

                    <AppIconButton
                      tooltipProps={{ title: "Download", placement: "top" }}
                      variant='contained'
                      size='large'
                      onClick={downloadFile}
                    >
                      <AppIcon of='download' fontSize='small' />
                    </AppIconButton>

                    {/* Close button */}
                    <AppIconButton onClick={onClose} variant='contained' size='large'>
                      <AppIcon of='close' fontSize='small' />
                    </AppIconButton>
                  </Stack>
                </Stack>

                <ZoomableBlock
                  wrapperRef={transformComponentRef}
                  allWrappersSx={{ overflow: "hidden" }}
                >
                  <FileItemView
                    showAIInsightsTags={false}
                    fileItem={selectedFile}
                    withCaption={false}
                    actions={{
                      enabled: false,
                      download: true,
                    }}
                    fullWidth
                    sx={{
                      width: "auto",
                      height: "100%",
                      overflow: "hidden",
                    }}
                    imageSx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </ZoomableBlock>
              </Box>

              {/* Caption */}
              <AppTypography sx={{ mt: 1 }}>{selectedFile.attachment?.caption}</AppTypography>
            </>
          )}
        </Box>

        {/* Files preview */}
        {fileItems.length > 1 && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{
              mt: "auto",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
                display: "grid",
                gridAutoFlow: "column",
                padding: 2,
                gridGap: "6px",
                webkitOverflowScrolling: "touch",
              }}
            >
              {fileItems.map((file, index) => (
                <Box sx={{ position: "relative" }} key={index}>
                  <FileItemView
                    sx={{
                      height: "100px",
                      width: "unset",
                      cursor: "pointer",
                    }}
                    fileItem={file}
                    actions={{
                      enabled: true,
                      click: true,
                      onClick: () => {
                        transformComponentRef?.current?.resetTransform();
                        selectFile(file);
                      },
                      ...(actions?.(file) || {}),
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
